import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'localization';
import { useAnalytics } from '@center-technologies/analytics';
import type { RootState } from '@center-pwa/store/store';

type AgentType = 'claude' | 'mcp' | 'webhook';
type AgentStatus = 'active' | 'deprecated' | 'inactive';

const MOCK_AGENTS: Array<{
  id: string;
  name: string;
  type: AgentType;
  status: AgentStatus;
}> = [
  { id: 'agent-1', name: 'Claude Assistant', type: 'claude', status: 'active' },
  { id: 'agent-2', name: 'Custom MCP Agent', type: 'mcp', status: 'active' },
  { id: 'agent-3', name: 'Legacy Webhook Bot', type: 'webhook', status: 'deprecated' },
];

const AgentsMockSection: React.FC = () => {
  const { t } = useTranslation();
  const logAnalyticsEvent = useAnalytics();

  const userId = useSelector((state: RootState) => state.userProfile?.userId);
  const identity = useSelector((state: RootState) => state.userProfile?.identity);
  const orgId = useMemo(() => {
    if (!identity?.users || !userId) return undefined;
    const currentUser = identity.users.find((u: any) => u.id === userId);
    if (!currentUser) return undefined;
    const org = Array.isArray(currentUser.organization)
      ? currentUser.organization[0]
      : currentUser.organization;
    return (org as any)?.id as string | undefined;
  }, [identity, userId]);

  useEffect(() => {
    if (!orgId) return;
    logAnalyticsEvent('org_settings_section_viewed', undefined, {
      sectionId: 'agents',
      orgId,
      isMocked: 'true',
    });
    logAnalyticsEvent('org_settings_mocks_section_clicked', undefined, {
      sectionId: 'agents',
      orgId,
    });
  }, [orgId, logAnalyticsEvent]);

  return (
    <div className="org-settings-section">
      <div className="org-settings-section__demo-banner" role="status" aria-live="polite">
        <i className="pi pi-info-circle" aria-hidden="true" />
        <span>{t('orgSettings.demoBanner')}</span>
      </div>

      <header className="org-settings-section__header">
        <h2 className="org-settings-section__title">{t('orgSettings.sections.agents')}</h2>
        <p className="org-settings-section__subtitle">{t('orgSettings.agents.connectedTitle')}</p>
      </header>

      <table
        className="org-settings-section__table"
        aria-label={t('orgSettings.agents.connectedTitle')}
      >
        <thead>
          <tr>
            <th scope="col">{t('orgSettings.agents.name')}</th>
            <th scope="col">{t('orgSettings.agents.type')}</th>
            <th scope="col">{t('orgSettings.agents.status')}</th>
          </tr>
        </thead>
        <tbody>
          {MOCK_AGENTS.map((agent) => (
            <tr key={agent.id}>
              <td>{agent.name}</td>
              <td>{t(`orgSettings.agents.typeValue.${agent.type}`)}</td>
              <td>{t(`orgSettings.agents.statusValue.${agent.status}`)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default AgentsMockSection;
